import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { useAccountCompanyId, useAccountLoading } from 'application/hooks';
import { usePaymentMethodRepositoryInjector } from 'application/providers/DIContainerProvider';
import { RouterUtils } from 'application/providers/RouterProvider';
import AnalyticsService, { EventNameAMP } from 'application/services/AnalyticsService';
import { BILLING_PAGE_PATH } from 'application/consts';
import { StateParamValues } from 'application/pages/BillingPage';
import CompanyUtils from 'application/utils/CompanyUtils';

import {
  AuthCurrentUserDocument,
  AuthCurrentUserQuery,
  BillingPaymentMethodDocument,
  BillingPaymentMethodQuery,
  PaymentMethodObjectPureType,
  useBillingCompanyBillingInfoQuery,
} from 'domain/api/graphql/generated';
import { FetchPolicyKeys } from 'domain/api/graphql/types';

import { UseCardInformationViewModel } from './types';

interface Props {
  setPaymentMethod: (paymentMethod: PaymentMethodObjectPureType | null) => void;
  setIsLinkingInProcess: (isLinkingInProcess: boolean) => void;
  setPaymentMethodLoading: (paymentMethodLoading: boolean) => void;
  openBillingBindCardModal: () => void;
  openBillingRemoveCardModal: (afterClose: () => void) => void;
  showBindErrorNotification: () => void;
}

const useCardInformationViewModel = ({
  setPaymentMethod,
  setIsLinkingInProcess,
  setPaymentMethodLoading,
  openBillingBindCardModal,
  openBillingRemoveCardModal,
  showBindErrorNotification,
}: Props): UseCardInformationViewModel => {
  const companyId = useAccountCompanyId();
  const userLoading = useAccountLoading();

  const { data: userData } = useQuery<AuthCurrentUserQuery>(AuthCurrentUserDocument, {
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const currentCompany = userData?.currentUserPureQuery?.mainCompany;
  const isSelfEmployed = CompanyUtils.isSelfEmployedOrganizationType(currentCompany?.requisite?.organizationType);

  const { getLinkForBindingCard, bindCardLoading, removeCardLoading } = usePaymentMethodRepositoryInjector();

  const { data, loading } = useBillingCompanyBillingInfoQuery({
    variables: {
      companyId,
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const { data: paymentMethodData, loading: paymentMethodLoadingResult } = useQuery<BillingPaymentMethodQuery>(
    BillingPaymentMethodDocument,
    {
      variables: { companyId },
      fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        AnalyticsService.event(EventNameAMP.BIND_CARD_ERROR, { error });
      },
    }
  );

  useEffect(() => {
    const paymentMethod = paymentMethodData?.paymentMethodPureQuery?.paymentMethod || null;
    const isLinkingInProcess = Boolean(paymentMethodData?.paymentMethodPureQuery?.isLinkingInProcess);

    setPaymentMethod(paymentMethod);
    setIsLinkingInProcess(isLinkingInProcess);
    setPaymentMethodLoading(paymentMethodLoadingResult);
  }, [paymentMethodData, paymentMethodLoadingResult, setPaymentMethod, setIsLinkingInProcess, setPaymentMethodLoading]);

  const onBindCardClick = async () => {
    AnalyticsService.event(EventNameAMP.BIND_CARD_PRESSED);

    const pathname = RouterUtils.generatePath(BILLING_PAGE_PATH, { state: StateParamValues.INFO });

    const link = await getLinkForBindingCard(`${window.location.origin}/${pathname}`, companyId);

    if (link) {
      window.location.assign(link);
    } else {
      showBindErrorNotification();
    }
  };

  const onRebindCardClick = () => {
    AnalyticsService.event(EventNameAMP.BIND_CARD_PRESSED);

    openBillingBindCardModal();
  };

  const onRemoveCardClick = () => {
    openBillingRemoveCardModal(() => {
      setPaymentMethod(null);
      setIsLinkingInProcess(false);
      setPaymentMethodLoading(false);
    });
  };

  const sberAuthButtonVisible =
    !isSelfEmployed && Boolean(currentCompany?.sberIdButtonState?.isVisible) && !userLoading;

  return {
    cardActionsLoading: bindCardLoading || removeCardLoading || loading,
    hasWaitPaymentLicenses: Boolean(data?.result?.hasWaitPaymentLicenses || data?.result?.isWaitPaymentLicense),
    sberAuthButtonVisible,
    onBindCardClick,
    onRebindCardClick,
    onRemoveCardClick,
  };
};

export default useCardInformationViewModel;
